import axios, { InternalAxiosRequestConfig, AxiosResponse } from 'axios';

const getDefaultApiBaseUrl = () => {
  if (typeof window === 'undefined') return 'http://localhost:8080';
  const { protocol, hostname, pathname } = window.location;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:8080`;
  }

  // Pre-producción: Cloud Run URL, subdominio ppro o sub-ruta /ppro en simildoc.app
  if (
    hostname.includes('simildoc-frontend-ppro') ||
    hostname === 'ppro.simildoc.app' ||
    (hostname.includes('simildoc.app') && pathname.startsWith('/ppro'))
  ) {
    return 'https://simildoc-backend-ppro-919932997379.europe-west1.run.app';
  }

  // Producción
  if (hostname.includes('simildoc-frontend-prod') || hostname === 'simildoc.app') {
    return 'https://simildoc-backend-prod-919932997379.europe-west1.run.app';
  }

  return `${protocol}//${hostname}`;
};

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || getDefaultApiBaseUrl(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const inflightGetRequests = new Map<string, Promise<AxiosResponse>>();

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const pproAuth = localStorage.getItem('ppro_auth');
  if (pproAuth) {
    config.headers.Authorization = `Basic ${pproAuth}`;
  } else {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  if (config.method?.toLowerCase() === 'get') {
    const paramsStr = config.params ? new URLSearchParams(config.params).toString() : '';
    const key = `${config.baseURL || ''}${config.url}?${paramsStr}`;

    if (inflightGetRequests.has(key)) {
      const existingPromise = inflightGetRequests.get(key)!;
      config.adapter = () => existingPromise;
    }
  }
  return config;
});

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.config.method?.toLowerCase() === 'get') {
      const paramsStr = response.config.params ? new URLSearchParams(response.config.params).toString() : '';
      const key = `${response.config.baseURL || ''}${response.config.url}?${paramsStr}`;
      if (!inflightGetRequests.has(key)) {
        inflightGetRequests.set(key, Promise.resolve(response));
        setTimeout(() => inflightGetRequests.delete(key), 1000);
      }
    }
    return response;
  },
  (error: any) => {
    if (error.config?.method?.toLowerCase() === 'get') {
      const paramsStr = error.config.params ? new URLSearchParams(error.config.params).toString() : '';
      const key = `${error.config.baseURL || ''}${error.config.url}?${paramsStr}`;
      setTimeout(() => inflightGetRequests.delete(key), 200);
    }

    if (error.response?.status === 401) {
      const detail = error.response.data?.detail;
      const wwwAuth = error.response.headers?.['www-authenticate'] || '';
      const isPpro =
        (typeof detail === 'string' && detail.includes('Pre-Production')) ||
        (typeof wwwAuth === 'string' && wwwAuth.includes('Pre-Production')) ||
        (typeof window !== 'undefined' && window.location.hostname.includes('ppro'));

      if (isPpro) {
        localStorage.removeItem('ppro_auth');
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('ppro-auth-required'));
        }
      }
    }

    return Promise.reject(error);
  }
);
